<script setup>
import { Drawer, useDrawer } from '@dashhudson/dashing-ui/drawer';
import { useFilterSidePanel } from '@dashhudson/dashing-ui/filter-side-panel';
import { useSecondaryNavigationPanel } from '@dashhudson/dashing-ui/secondary-navigation-panel';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import { computed, onMounted, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useGlobalModals } from '@/composables/useGlobalModals';
import { registerDrawers } from '@/drawerRegistry';
import { getGlobalModals } from '@/utils/globalModals';
import { isScreenXl } from '@/utils/tailwind';

const route = useRoute();
const router = useRouter();
const { stateKey, launchDrawer, closeDrawer, currentDrawer, parseStateKey } = useDrawer();
const { hasGlobalModal } = useGlobalModals();

registerDrawers();

watch(
  () => hasGlobalModal.value,
  () => {
    if (hasGlobalModal.value) {
      closeDrawer();
    }
  },
);

function isSameStateKey(leftStateKey, rightStateKey) {
  return isEqual(leftStateKey, rightStateKey);
}

function isSameDrawer(leftStateKey, rightStateKey) {
  const leftStateKeyObject = parseStateKey(leftStateKey);
  const rightStateKeyObject = parseStateKey(rightStateKey);
  return isEqual(leftStateKeyObject?.name, rightStateKeyObject?.name);
}

function changeDrawerQuery({ d, replace = false }) {
  const globalModalsOpen = getGlobalModals()?.length > 0;

  if (!globalModalsOpen) {
    // work around for global modals
    router[replace ? 'replace' : 'push']({
      ...route,
      query: {
        ...route.query,
        d,
      },
    });
  }
}

const pushOnPropChange = computed(() => {
  if (isFunction(currentDrawer.value?.meta?.pushHistoryOnPropChange)) {
    return currentDrawer.value.meta.pushHistoryOnPropChange();
  }
  return !!currentDrawer.value?.meta?.pushHistoryOnPropChange;
});

// Watch for stateKey changes and update the route query
watch(
  () => stateKey.value,
  (sk, oldSk) => {
    if (currentDrawer.value?.meta?.excludeFromRouter) {
      return;
    }

    if (!sk) {
      if (!hasGlobalModal.value) {
        changeDrawerQuery({ d: undefined });
      }
    } else if (!isSameDrawer(sk, oldSk) || pushOnPropChange.value) {
      changeDrawerQuery({ d: sk });
    } else if (!isSameStateKey(sk, route.query.d)) {
      changeDrawerQuery({ d: sk, replace: true });
    }
  },
);

function updateDrawerState() {
  if (isSameStateKey(stateKey.value, route.query.d)) {
    return;
  }

  if (route.query?.d && !route?.meta?.report) {
    launchDrawer({ ...route.query, stateKey: route.query.d });
  } else {
    closeDrawer();
  }
}

onMounted(() => {
  updateDrawerState();
});

// Watch a getter function instead of the route object directly.
// This ensures Vue's reactivity system can track the dependency properly,
// regardless of how useRoute() returns the route (ref, reactive, or plain object).
watch(
  () => route.fullPath,
  () => {
    updateDrawerState();
  },
);

const { expanded, collapseNavigationPanel, expandNavigationPanel } = useSecondaryNavigationPanel();

const currentSize = ref();

const isSecondaryNavAutoCollapsed = ref(false);

function onSize(size) {
  currentSize.value = size;
}

function onClose() {
  if (isSecondaryNavAutoCollapsed.value) {
    expandNavigationPanel();
    currentSize.value = undefined;
    isSecondaryNavAutoCollapsed.value = false;
  }
}

watch(
  () => [isScreenXl.value, currentDrawer.value, currentSize.value],
  () => {
    const isDrawerOpen = !!currentDrawer.value;
    const isSecondaryMenuOpen = expanded.value;
    const shouldCollapseSecondaryNav = !isScreenXl.value || currentSize.value === 'full';

    if (isDrawerOpen && isSecondaryMenuOpen && shouldCollapseSecondaryNav) {
      isSecondaryNavAutoCollapsed.value = true;
      collapseNavigationPanel();
    }
  },
);

router.afterEach((to, from) => {
  if (to?.path !== from?.path) {
    closeDrawer();
  }
});

const { visible: filterVisible } = useFilterSidePanel();

const drawerMaxWidth = computed(() => {
  let usedSpace = '(var(--primary-navigation-width) + 40px)';
  if (filterVisible.value) {
    usedSpace = 'var(--filter-side-panel-width)';
  } else if (expanded.value) {
    if (isScreenXl.value) {
      usedSpace = '(var(--combined-navigation-width) + 40px)';
    }
  }
  return `calc(100% - ${usedSpace})`;
});

const keepAliveInclude = ref([
  'CreatorProfile',
  'AccountProfile',
  'AssignDeliverables',
  'TopPerformingCreators',
  'AddCreatorsWizard',
  'DateRangeEditor',
  'EmailInvitation',
  'SocialListeningTrendingKeywords',
  'SocialListeningTopPerformingPosts',
  'SocialListeningVisualTrends',
  'SocialListeningVisualTrend',
  'SocialListeningTopicTemplateCreator',
  'SocialListeningIntuitiveTopicCreation',
  'CreateEditCampaign',
  'EventDrawer',
  'SocialListeningVisualFilters',
  'PinterestBulkDrawerContent',
  'DashboardTemplates',
  'MediaItemsDrawer',
  'ProductOrders',
]);

/**
 * Configure's the Drawer's KeepAlive component.
 *
 * You can read up on how KeepAlive works in the vue docs:
 * https://vuejs.org/guide/built-ins/keep-alive
 */
const keepAlive = computed(() => ({
  include: keepAliveInclude.value,
}));

provide('DrawerProviderService', {
  addKeepAlive: (name) => {
    if (!keepAliveInclude.value.includes(name)) {
      keepAliveInclude.value.push(name);
    }
  },
  removeKeepAlive: (name) => {
    const index = keepAliveInclude.value.indexOf(name);
    if (index !== -1) {
      keepAliveInclude.value.splice(index, 1);
    }
  },
});
</script>

<template>
  <Drawer
    :keep-alive="keepAlive"
    :style="{
      maxWidth: drawerMaxWidth,
    }"
    @size="onSize"
    @close="onClose"
  />
</template>

<style lang="postcss">
[data-dash-drawer] {
  top: var(--dash-top-banner-offset, 0) !important;
}
</style>
